import { BriefcaseIcon } from "@heroicons/react/outline";
import { CheckIcon } from "@radix-ui/react-icons";
import { FolderOpen } from "lucide-react";
import * as React from "react";

import { Badge, BadgeVariant } from "@/components/ui/Badge";
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown";
import { Organization } from "@/types/organizations";
import { Workspace } from "@/types/workspaces";

interface WorkspacesListProps {
  organizations: Organization[];
  workspaces: Workspace[];
  activeOrganizationId?: number;
  activeWorkspaceId?: number;
  defaultOrganizationId?: number;
  onWorkspaceSelect: (workspaceId: number) => void;
  onOrganizationSelect: (organizationId: number) => void;
  getDefaultWorkspaceId: (organizationId: number) => number | undefined;
}

export const WorkspacesList: React.FC<WorkspacesListProps> = ({
  organizations,
  workspaces,
  activeOrganizationId,
  activeWorkspaceId,
  defaultOrganizationId,
  onWorkspaceSelect,
  onOrganizationSelect,
  getDefaultWorkspaceId,
}) => {
  const sortedOrganizations = React.useMemo(
    () =>
      [...organizations].sort((a, b) => {
        const aIsDefault = a.id === defaultOrganizationId;
        const bIsDefault = b.id === defaultOrganizationId;
        if (aIsDefault && !bIsDefault) {
          return -1;
        }
        if (!aIsDefault && bIsDefault) {
          return 1;
        }
        return a.name.localeCompare(b.name);
      }),
    [organizations, defaultOrganizationId],
  );

  const getOrgWorkspaces = (organizationId: number) =>
    workspaces.filter(workspace => workspace.organization_id === organizationId);

  const getSortedWorkspaces = (orgWorkspaces: Workspace[], organizationId: number) =>
    [...orgWorkspaces].sort((a, b) => {
      const aIsDefault = a.id === getDefaultWorkspaceId(organizationId);
      const bIsDefault = b.id === getDefaultWorkspaceId(organizationId);
      if (aIsDefault && !bIsDefault) {
        return -1;
      }
      if (!aIsDefault && bIsDefault) {
        return 1;
      }
      return a.name.localeCompare(b.name);
    });

  return (
    <>
      <DropdownMenuLabel className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
        Workspaces
      </DropdownMenuLabel>

      {sortedOrganizations.map(organization => {
        const orgWorkspaces = getOrgWorkspaces(organization.id);
        const isActiveOrg = activeOrganizationId === organization.id;
        const sortedWorkspaces = getSortedWorkspaces(orgWorkspaces, organization.id);

        return (
          <DropdownMenuSub key={organization.id}>
            <DropdownMenuSubTrigger className="mx-1 gap-2 rounded-md px-2 py-1.5 text-gray-700 hover:bg-gray-50">
              <div className="flex size-5 flex-shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-gray-100 to-gray-50 ring-1 ring-gray-200/50">
                <span className="text-[10px] font-semibold text-gray-600">
                  {organization.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <span
                className={`flex-1 truncate text-sm ${isActiveOrg ? "font-semibold text-gray-900" : "text-gray-700"}`}
              >
                {organization.name}
              </span>
              {isActiveOrg && <CheckIcon className="size-4 flex-shrink-0 text-blue-600" />}
              {organization.id === defaultOrganizationId && !isActiveOrg && (
                <Badge variant={BadgeVariant.BLUE} className="flex-shrink-0 px-1.5 py-0 text-[9px]">
                  Default
                </Badge>
              )}
            </DropdownMenuSubTrigger>

            <DropdownMenuSubContent className="min-w-[200px] max-w-[260px] rounded-xl p-1">
              {sortedWorkspaces.length > 0 ? (
                sortedWorkspaces.map(workspace => {
                  const isActiveWorkspace = activeWorkspaceId === workspace.id;
                  const isDefaultWorkspace =
                    workspace.id === getDefaultWorkspaceId(organization.id);

                  return (
                    <DropdownMenuItem
                      key={workspace.id}
                      onClick={() => onWorkspaceSelect(workspace.id)}
                      className="gap-2 rounded-md px-2 py-1.5"
                    >
                      <div className="flex size-4 items-center justify-center rounded bg-gray-100">
                        <BriefcaseIcon className="size-2.5 text-gray-500" />
                      </div>
                      <span
                        className={`flex-1 truncate text-sm ${isActiveWorkspace ? "font-semibold text-gray-900" : "text-gray-700"}`}
                      >
                        {workspace.name}
                      </span>
                      {isDefaultWorkspace && !isActiveWorkspace && (
                        <Badge
                          variant={BadgeVariant.GRAY}
                          className="flex-shrink-0 px-1.5 py-0 text-[9px]"
                        >
                          Default
                        </Badge>
                      )}
                      {isActiveWorkspace && (
                        <CheckIcon className="size-4 flex-shrink-0 text-blue-600" />
                      )}
                    </DropdownMenuItem>
                  );
                })
              ) : (
                <DropdownMenuItem
                  onClick={() => onOrganizationSelect(organization.id)}
                  className="gap-2 rounded-md px-2 py-1.5 text-gray-500"
                >
                  <FolderOpen className="size-4" />
                  <span className="text-sm">No workspaces</span>
                </DropdownMenuItem>
              )}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        );
      })}

      <DropdownMenuSeparator className="my-1" />
    </>
  );
};
